import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { useNotifications } from '@decisiv/ui-components';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Tabs, { Tab } from '@decisiv/ui-components/lib/components/Tabs';
import { H1, P } from '@decisiv/ui-components/lib/components/Typography';

import { createSortHandler } from 'components/pages/Users/utils/sortUtils';
import Toasts from 'components/Toasts';

import AccessModal from './AccessModal';
import BulkCreateUserWizard from './BulkCreateUserWizard';
import { TOASTS } from './BulkCreateUserWizard/copy';
import bulkImportToast from './bulkImportToast';
import CreateUserButton from './CreateUserButton';
import CreateUserWizard from './CreateUserWizard';
import UsersFilters from './UsersFilters';
import UsersSearch from './UsersSearch';
import UsersTable from './UsersTable';
import { ACCESS_TABS } from './accessCopy';
import { PageBody, PageHeader, TabsArea } from './styles';
import { PageSurface } from '../../shared/styles';
import useFilterOptions from '../hooks/useFilterOptions';
import useUserAccess, { REMOVE, RESTORE } from '../hooks/useUserAccess';
import useUserEvents from '../hooks/useUserEvents';
import useUsers from '../hooks/useUsers';
import {
  CURRENT_ACCESS,
  REMOVED_ACCESS,
  readUrlState,
  writeUrlState,
} from '../urlState';

/**
 * Everything below the Management shell on /management/users.
 *
 * All of the page's state lives here rather than in Users.jsx, and that split is
 * load-bearing: KeyDS LeftNav creates styled components inside a useMemo, so its
 * hook count changes between renders and re-rendering it throws "Rendered fewer
 * hooks than expected" on the React 16 portal pins (same root cause as the
 * defaultNavExpandedState note in ManagementLayout). Keeping the state here means
 * a fetch only re-renders this subtree, never the nav.
 */
const TOAST_DURATION = 8000;

function UsersContent({ canEdit }) {
  const initial = useRef(readUrlState()).current;

  const [access, setAccess] = useState(initial.access);
  const [page, setPage] = useState(initial.page);
  const [sortBy, setSortBy] = useState(initial.sortBy);
  const [sortOrder, setSortOrder] = useState(initial.sortOrder);
  const [filters, setFilters] = useState(initial.filters);
  const [query, setQuery] = useState(initial.query);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isBulkWizardOpen, setIsBulkWizardOpen] = useState(false);
  // Bumped when the wizard creates someone, which is what pulls them into the
  // list — the effect below refetches on it. It also re-asks for the account
  // filter options, since a user created on an account that had none makes that
  // account an option for the first time.
  const [createdCount, setCreatedCount] = useState(0);
  // Bumped when access is removed or restored, which moves users between the
  // two tabs. Shares the refetch effect with createdCount for the same reason.
  const [accessCount, setAccessCount] = useState(0);

  const { notify } = useNotifications();
  const { users, isLoading, error, pagination, fetchUsersData } = useUsers();

  const userAccess = useUserAccess({
    onChanged: useCallback(() => setAccessCount((count) => count + 1), []),
  });

  const isRemoved = access === REMOVED_ACCESS;

  // Back to the first page, like every other narrowing: the two tabs are
  // different result sets and page 3 of one rarely exists in the other.
  const onAccessChange = (next) => {
    setAccess(next);
    setPage(1);
  };

  // Where the design's toasts come from. An import runs in a worker and the
  // wizard invites the admin to close it, so the outcome arrives here — on
  // whatever page they happen to be on — rather than being held by the wizard
  // that started it. Bulk-created users are new rows, so this also refetches.
  const onBulkImportEvents = useCallback(
    (events) => {
      const toasts = events.map(bulkImportToast).filter(Boolean);
      if (!toasts.length) return;

      toasts.forEach(({ body, intent, reportPath, title }) =>
        notify({
          intent,
          title,
          duration: TOAST_DURATION,
          // KeyDS renders the body through a render prop, and its actions take
          // an onClick rather than an href — hence the navigation by hand.
          children: () => <P style={{ margin: 0 }}>{body}</P>,
          actions: reportPath
            ? [
                {
                  text: TOASTS.download,
                  onClick: () => window.location.assign(reportPath),
                },
              ]
            : [],
          onClose: () => {},
        }),
      );
      setCreatedCount((count) => count + 1);
    },
    [notify],
  );

  // Paused while the bulk wizard is open: it reports the outcome itself on its
  // Summary step, and the standard is that the admin is told once — in the
  // modal if they stayed, as a toast if they closed it. The wizard consumes its
  // own event (see consumeImportEvents), so re-enabling on close raises a toast
  // only for an import this page never showed.
  useUserEvents(onBulkImportEvents, { enabled: !isBulkWizardOpen });
  const {
    accountOptions,
    accountsTotal,
    hasMoreAccounts,
    isLoadingAccounts,
    jobRoleOptions,
    loadMoreAccounts,
    permissionOptions,
    searchAccounts,
  } = useFilterOptions(createdCount);

  const onSort = createSortHandler((column) => {
    setSortBy(column);
    setPage(1);
  }, setSortOrder);

  // Narrowing the results can leave the current page past the end, so both
  // filtering and searching return to the first page.
  const onFiltersChange = (next) => {
    setFilters(next);
    setPage(1);
  };

  const onSearch = (term) => {
    setQuery(term);
    setPage(1);
  };

  // Which of the two empty states to show turns on this.
  const isNarrowed =
    query.length > 0 ||
    Object.values(filters).some((values) => values.length > 0);

  useEffect(() => {
    fetchUsersData({ access, filters, page, query, sortBy, sortOrder });
  }, [
    access,
    accessCount,
    createdCount,
    fetchUsersData,
    filters,
    page,
    query,
    sortBy,
    sortOrder,
  ]);

  // The address bar tracks page, sort, filters and the search term so a reload
  // lands on the same view.
  useEffect(() => {
    writeUrlState({ access, filters, page, query, sortBy, sortOrder });
  }, [access, filters, page, query, sortBy, sortOrder]);

  return (
    <PageSurface>
      <PageHeader>
        <Flex alignItems="flex-start" justifyContent="space-between">
          <Flex flexDirection="column">
            <H1 style={{ margin: 0 }}>Users</H1>
            <Flex marginTop={1}>
              <CreateUserButton
                onCreateMultipleUsers={() => setIsBulkWizardOpen(true)}
                onCreateUser={() => setIsWizardOpen(true)}
              />
            </Flex>
          </Flex>
          <UsersSearch value={query} onSearch={onSearch} />
        </Flex>
      </PageHeader>
      <TabsArea>
        <Tabs
          onChange={onAccessChange}
          renderHiddenTabsContent={false}
          selectedId={access}
          size="medium"
        >
          <Tab id={CURRENT_ACCESS} title={ACCESS_TABS.current} />
          <Tab id={REMOVED_ACCESS} title={ACCESS_TABS.removed} />
        </Tabs>
      </TabsArea>
      <UsersFilters
        accountOptions={accountOptions}
        accountsTotal={accountsTotal}
        filters={filters}
        hasMoreAccounts={hasMoreAccounts}
        isLoadingAccounts={isLoadingAccounts}
        jobRoleOptions={jobRoleOptions}
        permissionOptions={permissionOptions}
        onChange={onFiltersChange}
        onLoadMoreAccounts={loadMoreAccounts}
        onSearchAccounts={searchAccounts}
      />
      <PageBody>
        <UsersTable
          canEdit={canEdit}
          error={error}
          isLoading={isLoading}
          isNarrowed={isNarrowed}
          isRemoved={isRemoved}
          onPageChange={setPage}
          onRemoveAccess={(row) => userAccess.open(REMOVE, row)}
          onRestoreAccess={(row) => userAccess.open(RESTORE, row)}
          onSort={onSort}
          pagination={pagination}
          query={query}
          sortBy={sortBy}
          sortOrder={sortOrder}
          users={users}
        />
      </PageBody>
      <AccessModal
        accountOptions={userAccess.accountOptions}
        action={userAccess.action}
        error={userAccess.error}
        isLoading={userAccess.isLoading}
        isSubmitting={userAccess.isSubmitting}
        onChangeSelected={userAccess.setSelectedIds}
        onClose={userAccess.close}
        onSubmit={userAccess.submit}
        selectedIds={userAccess.selectedIds}
        user={userAccess.user}
        visible={userAccess.isOpen}
      />

      <CreateUserWizard
        visible={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        onCreated={() => setCreatedCount((count) => count + 1)}
      />
      <BulkCreateUserWizard
        visible={isBulkWizardOpen}
        onClose={() => setIsBulkWizardOpen(false)}
        onCompleted={() => setCreatedCount((count) => count + 1)}
      />

      <Toasts />
    </PageSurface>
  );
}

UsersContent.propTypes = {
  canEdit: PropTypes.bool,
};

UsersContent.defaultProps = {
  canEdit: false,
};

export default UsersContent;
